import { SHInputAdornment } from "@components/design-systems";
import {
  SHNumericFieldV2,
  SHNumericFieldV2Props,
} from "@components/design-systems/sh-numeric-field-v2";
import { Dollar2SVG } from "@components/svgs";
import { AnalysisFeeCalculatorDTO } from "@models/platform-analysis/entities/steps/fee";
import { FeeCalculatorRenderFieldType } from "@models/platform-analysis/enums/fee/renderFieldType";
import { isNil } from "lodash";
import { Controller, FieldPath, useFormContext } from "react-hook-form";
import { useUnmount } from "react-use";
import { AnalysisFeeCalculatorStepConfigs as Configs } from "../../../configs";

type Props = {
  fieldType?: FeeCalculatorRenderFieldType;
  fieldPath: FieldPath<AnalysisFeeCalculatorDTO>;
  label?: string;
  min?: number;
  max?: number;
  convertToNumericString?: boolean;
  resetOnUnmount: boolean;
  disabled?: boolean;
  handleOnValueChange?: (value?: number) => void;

  // internals
  isNumber?: boolean;
  isCurrency?: boolean;
} & SHNumericFieldV2Props;

export const FeeCalculatorNumericInputController = ({
  fieldType,
  fieldPath,
  label,
  min = Configs.MinAccountBalance,
  max = Configs.MaxAccountBalance,
  disabled = false,
  convertToNumericString = true,
  resetOnUnmount = false,
  isNumber = fieldType === FeeCalculatorRenderFieldType.NumberInput,
  isCurrency = fieldType === FeeCalculatorRenderFieldType.CurrencyInput,
  handleOnValueChange,
  ...others
}: Props) => {
  const { control, getValues, setValue } =
    useFormContext<AnalysisFeeCalculatorDTO>();

  const handleReset = (resetCase: "blur" | "focus") => {
    const value = getValues(fieldPath);
    const numberValue = Number(value);
    if (resetCase === "focus" && (numberValue === 0 || isNil(value))) {
      setValue(fieldPath, "");
      return;
    }

    if (numberValue !== 0 && !isNaN(numberValue)) return;
    setValue(
      fieldPath,
      convertToNumericString
        ? String(Configs.MinAccountBalance)
        : Configs.MinAccountBalance,
    );
  };

  useUnmount(() => {
    if (!resetOnUnmount) return;
    const defaultValue = convertToNumericString
      ? String(Configs.MinAccountBalance)
      : Configs.MinAccountBalance;
    setValue(fieldPath, defaultValue);
  });

  return (
    <Controller
      control={control}
      name={fieldPath}
      render={({ field: { onChange, value } }) => (
        <SHNumericFieldV2
          {...others}
          fullWidth
          thousandSeparator
          valueIsNumericString
          disabled={disabled}
          value={String(value)}
          min={min}
          max={max}
          label={label}
          decimalScale={isNumber ? 0 : undefined}
          onValueChange={({ floatValue }) => {
            const newValue =
              isNil(floatValue) || isNaN(floatValue)
                ? convertToNumericString
                  ? String(Configs.MinAccountBalance)
                  : Configs.MinAccountBalance
                : convertToNumericString
                ? String(floatValue)
                : floatValue;
            onChange(newValue);
            handleOnValueChange?.(floatValue);
          }}
          onFocus={() => handleReset("focus")}
          onBlur={() => handleReset("blur")}
          InputProps={{
            startAdornment: isCurrency ? (
              <SHInputAdornment position="start">
                <Dollar2SVG />
              </SHInputAdornment>
            ) : undefined,
          }}
          sx={{
            "& .MuiInput-root": {
              "&::before,&::after": {
                left: isCurrency ? "30px" : undefined,
              },
            },
          }}
        />
      )}
    />
  );
};
